// Single source of truth for anchoring "now" to a calendar day in Asia/Beirut.
//
// Lebanon is UTC+2/+3, so between local midnight and ~02:00-03:00 a UTC-derived
// "today" is still yesterday. Event bucketing (upcoming/past/today/weekend) is
// computed in four places (catalog filtering, EventsScreen, Guide "next up", the
// ask-Zgharta planner ranking) that must all agree — so they all route through
// here. Event `date` fields are date-only strings (e.g. `2026-08-15`), parsed as
// UTC midnight, whose own day key is therefore stable; only the now->day bucket
// shifts, which this corrects.
//
// DST-safe by construction: `Intl` resolves the Beirut offset per-date, so a
// fixed `+02:00`/`+03:00` shortcut is never taken.

const BEIRUT_TIME_ZONE = 'Asia/Beirut';

// One formatter instance, reused. `en-CA` yields `YYYY-MM-DD`, but we assemble
// the key from parts rather than trusting the formatted string's layout.
const beirutDayPartsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BEIRUT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// Returns the `YYYY-MM-DD` calendar day of `value` in Asia/Beirut, or `null`
// for an unparseable value. Keys sort chronologically under lexicographic
// comparison, so `a < b`/`a >= b`/`a === b` are valid day comparisons.
export function beirutDayKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  if (typeof value === 'string') {
    const text = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text) && date.toISOString().slice(0, 10) !== text) return null;
  }
  const parts = beirutDayPartsFormatter.formatToParts(date);
  const lookup = {};
  for (const part of parts) {
    if (part.type !== 'literal') lookup[part.type] = part.value;
  }
  if (!lookup.year || !lookup.month || !lookup.day) return null;
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

// Returns the UTC-midnight epoch ms of `value`'s Beirut calendar day, or `null`
// for an unparseable value. The returned instant is a stable per-day anchor for
// whole-day arithmetic (leg counts, weekend/month ranges); it is NOT the actual
// Beirut midnight instant, just a canonical UTC anchor for the calendar day.
export function beirutDayStartUtcMs(value) {
  const key = beirutDayKey(value);
  if (key == null) return null;
  const [year, month, day] = key.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

function toValidEventDate(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string' || value.trim() === '') return null;

  const text = value.trim();
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text) && date.toISOString().slice(0, 10) !== text) {
    return null;
  }
  return date;
}

// Event dates are date-only calendar values owned by Beirut, not instants that
// should move when a visitor's browser uses another timezone.
export function formatBeirutEventDate(value, locale = 'en-US', options = {}) {
  const date = toValidEventDate(value);
  if (!date) return null;

  try {
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: BEIRUT_TIME_ZONE,
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * A short date label with its parts joined in a fixed order (#450).
 *
 * `Intl` orders a PARTIAL field set by its own rules: `en-US` renders `{weekday, day}` as "1 Wed"
 * and `{weekday, day, month}` as "Sat, Jul 4", which reads as noise inside a run caption. The
 * parts are therefore formatted one at a time and joined weekday-day-month — the order the design
 * asks for and the order Arabic reads them in. Shared by the visitor Events card, the event
 * detail, and the dashboard's visitor preview, so the three cannot drift.
 */
export function eventShortDayLabel(value, locale = 'en-US', { weekday = false, month = false, year = false } = {}) {
  const parts = [];
  if (weekday) parts.push(formatBeirutEventDate(value, locale, { weekday: 'short' }));
  parts.push(formatBeirutEventDate(value, locale, { day: 'numeric' }));
  if (month) parts.push(formatBeirutEventDate(value, locale, { month: 'short' }));
  if (year) parts.push(formatBeirutEventDate(value, locale, { year: 'numeric' }));
  return parts.filter(Boolean).join(' ');
}

/**
 * Whether a date range has to state its year(s), and on which endpoint (#450).
 *
 * A short range inside this year does not need one — "Runs Wed 1 – Sat 4 Jul" is unambiguous in
 * July. Everything else does: a run in another year, and a run that crosses New Year, where
 * leaving the year off makes the end look like it comes BEFORE the start. The single-day path
 * has always carried the year, so a range that drops it is the inconsistency, not the fix.
 */
export function eventRangeYearParts(start, end, locale = 'en-US', now = new Date()) {
  const currentYear = formatBeirutEventDate(now, locale, { year: 'numeric' });
  const startYear = formatBeirutEventDate(start, locale, { year: 'numeric' });
  const endYear = formatBeirutEventDate(end, locale, { year: 'numeric' });
  const crossesYear = Boolean(startYear && endYear) && startYear !== endYear;
  const outsideThisYear = Boolean(endYear) && endYear !== currentYear;
  return { start: crossesYear, end: crossesYear || outsideThisYear };
}

/**
 * The `dates=` endpoints for a Google Calendar all-day link, as `YYYYMMDD` strings (#409).
 *
 * Google reads the END of an all-day range as EXCLUSIVE, so the last day a visitor should see
 * is `end + 1`. Two bugs live here if this is done by hand: passing the start date as both
 * endpoints (what this app did) publishes a multi-day festival as its first day only, and
 * passing `end` unshifted silently drops the festival's last day.
 *
 * Returns null for an unreadable start date or a supplied unreadable end date, so the caller can
 * render no link rather than a calendar entry on the wrong day. A missing end makes a one-day
 * event; an end before the start is ignored, never trusted.
 */
export function googleCalendarAllDayRange(start, end) {
  // `beirutDayKey` alone is not a guard here: `new Date(null)` is the epoch, so a missing date
  // would publish a calendar entry on 1 January 1970. `toValidEventDate` refuses null, the
  // empty string and any non-date value first.
  const startDate = toValidEventDate(start);
  if (!startDate) return null;
  const startKey = beirutDayKey(startDate);
  if (!startKey) return null;
  const endIsMissing = end == null || (typeof end === 'string' && end.trim() === '');
  const endDate = endIsMissing ? null : toValidEventDate(end);
  if (!endIsMissing && !endDate) return null;
  const endKey = endDate ? beirutDayKey(endDate) : null;
  const lastDay = endKey && endKey > startKey ? endKey : startKey;
  const [year, month, day] = lastDay.split('-').map(Number);
  const exclusive = new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
  return { start: startKey.replace(/-/g, ''), end: exclusive.replace(/-/g, '') };
}
